<template>
  <div id="arranger" ref="arranger">
    <div id="flap" class="tabs">
      <div>
        <button
          class="tab selector"
          :class="{ 'open': showFilters }"
          @click.prevent="showFilters = !showFilters"
        >
          <span>{{ getSelectedFilterName }}</span>
        </button>
      </div>
      <transition name="slide-vertical">
        <div v-if="showFilters">
          <button class="tab" :class="getBtnClass('all')" @click="selectFilter('all')">
            {{ $t('common.all') }}
          </button>
          <button class="tab" :class="getBtnClass('orders')" @click="selectFilter('orders')">
            {{ $t('common.transfers') }}
          </button>
          <button class="tab" :class="getBtnClass('bills')" @click="selectFilter('bills')">
            {{ $t('common.bills') }}
          </button>
          <button class="tab" :class="getBtnClass('payouts')" @click="selectFilter('payouts')">
            {{ $t('common.payout') }}
          </button>
        </div>
      </transition>
    </div>
    <transition name="fromRight">
      <div
        v-show="showSearchBlock"
        id="forage"
        :class="{ 'collapsed': !showSearchBlock }"
      >
        <div>
          <input
            id="search"
            ref="searchText"
            v-model.trim="searchText"
            type="text"
            maxlength="64"
            spellcheck="false"
            :placeholder="$t('common.search')"
            :title="$t('search.by.transactions')"
            @focus="$emit('toggle-block')"
            @blur="$emit('toggle-block')"
          />
          <label for="search"></label>
        </div>
      </div>
    </transition>
    <button @click.prevent="toggleSearchBlock"></button>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import throttle from 'lib/utils/throttle';

const AMOUNT_REG_EXP = /^-\d+/;

export default {
  name: 'TransactionFilters',
  mounted() {
    this.showFilters = this.isDeviceSize.laptop;

    if (!this.$route.query.public_id) {
      return;
    }

    this.searchText = this.$route.query.public_id;
    this.showSearchBlock = true;
  },
  data() {
    return {
      searchText: '',
      showFilters: true,
      showSearchBlock: false,
    };
  },
  computed: {
    getSelectedFilterName() {
      const FILTERS_MAP = {
        all: this.$t('common.all'),
        orders: this.$t('common.transfers'),
        bills: this.$t('common.bills'),
        payouts: this.$t('common.payout'),
      };

      return FILTERS_MAP[this.TRANSACTIONS.filterBy];
    },
    preparedSearchText() {
      if (AMOUNT_REG_EXP.test(this.searchText)) {
        return this.searchText.replace(/^-/, '');
      }

      return encodeURIComponent(this.searchText);
    },
    ...mapState(['TRANSACTIONS', 'SCROLL_STATES']),
    ...mapGetters(['isDeviceSize']),
  },
  watch: {
    searchText() {
      this.processSearching();
    },
    // isDeviceSize(value) {
    //   this.showFilters = value;
    // },
    TRANSACTIONS: {
      deep: true,
      handler() {
        if (!this.$route.query.public_id) {
          return;
        }

        if (this.TRANSACTIONS.result.length) {
          this.SELECT_TRANSACTION(this.TRANSACTIONS.result[0]);
        }
      },
    },
  },
  methods: {
    getBtnClass(filterName) {
      return this.TRANSACTIONS.filterBy === filterName ? 'active' : '';
    },
    async selectFilter(filterName) {
      if (!this.isDeviceSize.laptop) {
        this.showFilters = false;
      }

      this.CLOSE_TRANSACTION();

      this.SET_TRANSACTIONS_PARAMS({
        filterBy: filterName,
      });

      this.GET_TRANSACTIONS({
        page: 1,
        reset: true,
      });

      if (!this.isDeviceSize.laptop || this.SCROLL_STATES.Filters_block_is_fixed) {
        this.goTop();
      }

      this.clearQueryUrl();
    },
    toggleSearchBlock() {
      if (this.showSearchBlock) {
        this.searchText = '';
      } else {
        setTimeout(() => {
          this.$refs.searchText.focus();
        }, 500);
      }

      this.showSearchBlock = !this.showSearchBlock;
    },
    goTop() {
      if (this.SCROLL_STATES.Filters_block_is_fixed) {
        this.$moveTo({ tolerance: -100 }).move(document.getElementById('appliance'));
      } else {
        this.$moveTo().move(document.getElementById('arranger'));
      }
    },
    clearQueryUrl() {
      if (!this.$route.query.public_id || !this.$route.query.subscriberId) {
        return;
      }

      this.$router.push({ name: 'cabinet', query: {} });
    },
    // eslint-disable-next-line prefer-arrow-callback
    processSearching: throttle(function () {
      this.CLOSE_TRANSACTION();

      this.SET_TRANSACTIONS_PARAMS({
        searchText: this.preparedSearchText,
      });

      this.GET_TRANSACTIONS({
        page: 1,
        reset: true,
      });

      if (!this.isDeviceSize.laptop || this.SCROLL_STATES.Filters_block_is_fixed) {
        this.goTop();
      }
    }, 500),
    ...mapActions(['GET_TRANSACTIONS']),
    ...mapMutations(['CLOSE_TRANSACTION', 'SET_TRANSACTIONS_PARAMS', 'SELECT_TRANSACTION']),
  },
};
</script>

<style lang="sass" scoped>
  #arranger
    position: sticky
    top: 0
    display: flex
    padding: 20px
    border-top: 1px solid transparent
    border-bottom: 1px solid #27283C
    overflow: hidden
    z-index: 100

    +respond-to('small')
      // margin: 0 20px 20px
      // padding: 20px 0

    +respond-to('medium')
      margin: 20px
      padding: 24px 0

    #{theme("eclipse")} &
      border-bottom-color: #cdcdcd
      background-color: #e8e8e8

      +respond-to('medium')
        border-top-color: #cdcdcd

    #{theme("obsidian")} &
      border-bottom-color: #27283C
      background-color: #1E1D2F

      +respond-to('medium')
        border-top-color: #27283C

    &.fixed
      position: fixed
      z-index: 9999
      top: 0
      width: 100%
      margin: 0
      background: #1e1c2f

    > button
      width: 20px
      height: 20px
      position: absolute
      top: 30px
      right: 20px
      background-color: transparent
      background-repeat: no-repeat
      background-position: center
      background-size: cover
      transform: translateY(-50%)
      z-index: 2
      cursor: pointer

      +respond-to('medium')
        top: 50%

      #{theme("eclipse")} &
        background-image: get-icon("search", #212121)

      #{theme("obsidian")} &
        background-image: get-icon("search", $pure-white)

      &:hover
        background-image: get-icon("search", #999999)

  #flap

    > div > button
      width: calc(100% - (1.24rem * 2))
      text-align: left

      +respond-to("small-medium")
        width: unset
        text-align: center

      &.selector
        width: auto
        padding: 0
        overflow: visible
        border: none

        &.open
          margin-bottom: 20px

        &::after
          +pseudo-block
          top: 0
          right: -30px
          width: 20px
          height: 20px
          background-image: url('~@/assets/img/icon-chevron-down--white.svg')
          background-repeat: no-repeat
          background-position: center
          background-size: cover

        +respond-to('small-medium')
          display: none
          visibility: hidden

        &.open
          &::after
            background-image: url('~@/assets/img/icon-chevron-up--white.svg')

        &:hover,
        &:active
          border-color: transparent
          background-color: transparent
          background-image: none

  #forage
    position: absolute

    +respond-to('medium')
      width: 100%

    input
      font-size: 1rem
      font-weight: 500
      line-height: 1.44
      width: 100%
      padding: 0 30px

      +respond-to('medium')
        padding: 10px 30px

      #{theme("eclipse")} &
        color: #212121
        background-color: #e8e8e8

      #{theme("obsidian")} &
        color: $pure-white
        background-color: #1D1D2F

    label
      width: 20px
      height: 20px
      position: absolute
      top: 50%
      left: 0
      background-repeat: no-repeat
      background-position: center
      background-size: cover
      transform: translateY(-50%)
      cursor: pointer

      #{theme("eclipse")} &
        background-image: get-icon("search", #212121)

      #{theme("obsidian")} &
        background-image: get-icon("search", $pure-white)

    &:not(.collapsed)

      ~ button
        #{theme("eclipse")} &
          background-image: get-icon("x", #212121)

        #{theme("obsidian")} &
          background-image: get-icon("x", $pure-white)

        &:hover
          background-image: get-icon("x", #999999)

  .tabs

    .tab
      position: relative
      background-color: transparent
      border-radius: 2.48rem
      padding: calc(.96rem * .3) calc(2.48rem * .6)
      border: 1px solid transparent
      transition: all 0.16s
      overflow: hidden

      &:not(:last-child)
        margin-right: 20px

      &.hover,
      &:hover
        border: 1px solid #76BEB0

      &.selected,
      &.active,
      &:active
        color: $pure-white
        background: linear-gradient(232.25deg, rgba(91, 204, 126, 0.7) -8.32%, rgba(123, 211, 255, 0.7) 108.85%), rgba(255, 255, 255, 0.5)

      &.loading
        cursor: wait
        color: transparent
        background-color: transparent
        box-shadow: unset

        &.hover,
        &:hover
          border: 1px solid transparent

        &.selected,
        &.active,
        &:active
          background-color: transparent

        &::after
          position: absolute
          content: ''
          display: block
          background-image: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, #3A3949 100%)
          left: 0
          right: 0
          top: 0
          bottom: 0

      &.disabled,
      &:disabled
        color: #8E8E97
        cursor: not-allowed

        &.hover,
        &:hover
          border: 1px solid transparent

        &.selected,
        &.active,
        &:active
          background-color: transparent
          border: 1px solid transparent
</style>
